import React, {
    forwardRef,
    memo,
    useCallback,
    useMemo,
    useReducer,
    useRef,
    useState,
    Fragment,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Grid } from 'react-window';
import { useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Popover from '../../utils/Popover';
import SearchBar from '../SearchBar';
import Text from '../../typography/Text';

import emojisData from './emojis.json';
import styles from './styles';

const useStyles = createUseStyles(styles, 'EmojiPicker');

const GRID_COLUMN_WIDTH = 36;
const GRID_ROW_HEIGHT = 32;
const GRID_COLS = 9;
const FREQUENTLY_USED_ITEMS_TO_SHOW = 36;

const DEFAULT_EMOJIS = emojisData.groups.flatMap(({ group, items }) =>
    items.map((item) => ({
        ...item,
        shortcodes: [item.name],
        group,
    })),
);

const CLEAR_SEARCH = 'CLEAR_SEARCH';
const SET_SEARCH = 'SET_SEARCH';
const SET_SELECTED = 'SET_SELECTED';
const SET_ACTIVE_SECTION = 'SET_ACTIVE_SECTION';

const initialState = {
    search: undefined,
    section: null,
    selectedIndex: null,
    columnIndex: 0,
    rowIndex: 0,
    hovered: false,
};

const reducer = (state, action) => {
    switch (action.type) {
        case SET_ACTIVE_SECTION:
            return { ...state, section: action.payload };
        case CLEAR_SEARCH:
            return {
                ...state,
                search: undefined,
                selectedIndex: null,
                columnIndex: 0,
                rowIndex: 0,
                hovered: false,
            };
        case SET_SEARCH:
            return {
                ...state,
                search: action.payload,
                selectedIndex: null,
                columnIndex: 0,
                rowIndex: 0,
                hovered: false,
            };
        case SET_SELECTED:
            return { ...state, ...action.payload };
        default:
            throw new Error();
    }
};

/**
 * Merge and sort emoji cache entries by frequency
 * Combines default values with runtime usage counts
 */
const buildFrequentlyUsedCache = (defaultCache = {}, runtimeCache = {}) => {
    const merged = { ...defaultCache, ...runtimeCache };
    return Object.fromEntries(
        Object.entries(merged)
            .sort((a, b) => b[1] - a[1])
            .slice(0, FREQUENTLY_USED_ITEMS_TO_SHOW),
    );
};

/**
 * Convert cache object to array of emoji objects
 */
const cacheToEmojiArray = (cache, availableEmojis) => {
    return Object.keys(cache).reduce((arr, name) => {
        const emoji = availableEmojis.find((item) => item.name === name);
        if (emoji) arr.push(emoji);
        return arr;
    }, []);
};

const EmojiPicker = forwardRef(
    (
        {
            children,
            classes: classesProp,
            onVisibleChange,
            popoverVisible,
            emojis,
            texts,
            defaultFrequentlyUsed,
            onSelectEmoji,
            onRequestFocus,
        },
        ref,
    ) => {
        const [state, dispatch] = useReducer(reducer, initialState);
        const [frequentlyUsedCache, setFrequentlyUsedCache] = useState(defaultFrequentlyUsed || {});
        const searchBarRef = useRef();
        const gridRef = useRef();
        const gridContainerRef = useRef();

        const availableEmojis = useMemo(
            () => (Array.isArray(emojis) && emojis.length ? emojis : DEFAULT_EMOJIS),
            [emojis],
        );

        const classes = useClasses(useStyles, classesProp);

        // --- Cache Management (Internal)
        const recordEmojiUsage = useCallback((emojiName) => {
            setFrequentlyUsedCache((prev) => ({
                ...prev,
                [emojiName]: (prev[emojiName] || 0) + 1,
            }));
        }, []);

        // --- Search Handling
        const handleChange = useCallback((value) => {
            let newSearch = value?.target?.value || value;
            newSearch = typeof newSearch === 'string' ? newSearch.toLowerCase() : undefined;
            dispatch({ type: SET_SEARCH, payload: newSearch });
        }, []);

        const handleVisibleChange = useCallback(
            (visible) => {
                onVisibleChange?.(visible);
                if (visible) {
                    setTimeout(() => searchBarRef?.current?.focus(), 400);
                } else {
                    dispatch({ type: CLEAR_SEARCH });
                }
            },
            [onVisibleChange],
        );

        const groupedEmojiObj = useMemo(() => {
            let emojiObj = availableEmojis
                .filter(({ name, tags, group }) => {
                    return state.search
                        ? name?.toLowerCase().startsWith(state.search) ||
                              tags?.find((tag) => tag.toLowerCase().startsWith(state.search)) ||
                              group?.toLowerCase().startsWith(state.search)
                        : true;
                })
                .reduce((obj, item) => {
                    if (item.group === 'components') return obj;
                    if (!obj[item.group]) obj[item.group] = [];
                    obj[item.group].push(item);
                    return obj;
                }, {});

            // Prepend "Frequently Used" section when not searching
            if (!state.search && Object.keys(frequentlyUsedCache).length > 0) {
                const sortedCache = buildFrequentlyUsedCache(
                    defaultFrequentlyUsed,
                    frequentlyUsedCache,
                );
                const frequentlyUsedEmojis = cacheToEmojiArray(sortedCache, availableEmojis);

                if (frequentlyUsedEmojis.length > 0) {
                    emojiObj = {
                        [texts?.frequently_used_emoji || 'Frequently Used']: frequentlyUsedEmojis,
                        ...emojiObj,
                    };
                }
            }

            return emojiObj;
        }, [
            availableEmojis,
            frequentlyUsedCache,
            defaultFrequentlyUsed,
            state.search,
            texts?.frequently_used_emoji,
        ]);

        // --- Grid Preparation
        const emojiGrid = useMemo(() => {
            return Object.keys(groupedEmojiObj).reduce((arr, groupName) => {
                const newEmojis = groupedEmojiObj[groupName];
                const newGroup = !state.search
                    ? [groupName, ...Array(GRID_COLS - 1).fill('dummy'), ...newEmojis]
                    : newEmojis;
                const n = GRID_COLS - (newEmojis.length % GRID_COLS);
                const dummyItems = n < GRID_COLS && !state.search ? Array(n).fill('dummy') : [];
                return [...arr, ...newGroup, ...dummyItems];
            }, []);
        }, [groupedEmojiObj, state.search]);

        const emojiGridRowsCount = useMemo(
            () => Math.floor(emojiGrid.length / GRID_COLS) + (emojiGrid.length % GRID_COLS ? 1 : 0),
            [emojiGrid.length],
        );

        // --- Selection Handlers
        const handleClick = useCallback(
            (name) => {
                const selectedEmoji = availableEmojis.find((item) => item.name === name);
                onSelectEmoji?.(name, selectedEmoji);
                onVisibleChange?.(false);
                dispatch({ type: CLEAR_SEARCH });
                recordEmojiUsage(name);
            },
            [availableEmojis, onSelectEmoji, onVisibleChange, recordEmojiUsage],
        );

        const handleHover = useCallback(
            ({ selectedIndex = null, columnIndex = 0, rowIndex = 0, hovered = false } = {}) =>
                dispatch({
                    type: SET_SELECTED,
                    payload: { selectedIndex, columnIndex, rowIndex, hovered },
                }),
            [],
        );

        // --- Grid Cell Renderer
        const GridItem = useCallback(
            ({ columnIndex, rowIndex, style }) => {
                let cellContent = null;
                const index = GRID_COLS * rowIndex + columnIndex;
                const gridItem = emojiGrid[index];

                switch (true) {
                    case typeof gridItem === 'string' && gridItem === 'dummy':
                        cellContent = <span />;
                        break;
                    case typeof gridItem === 'string':
                        cellContent = (
                            <Text type="captionMedium" className={classes.section}>
                                {gridItem}
                            </Text>
                        );
                        break;
                    case typeof gridItem === 'object':
                        cellContent = (
                            <span
                                className={classNames(classes.emojiItem, {
                                    [classes.emojiItemActive]:
                                        index === state.selectedIndex && !state.hovered,
                                    [classes.emojiItemHover]:
                                        index === state.selectedIndex && state.hovered,
                                })}
                                onClick={() => handleClick(gridItem.name)}
                                onMouseEnter={() =>
                                    handleHover({
                                        selectedIndex: index,
                                        columnIndex,
                                        rowIndex,
                                        hovered: true,
                                    })
                                }
                                onMouseLeave={() => handleHover()}
                            >
                                {gridItem?.emoji}
                            </span>
                        );
                        break;
                    default:
                        break;
                }
                return <div style={style}>{cellContent}</div>;
            },
            [classes, emojiGrid, handleClick, handleHover, state.hovered, state.selectedIndex],
        );

        const selectedEmoji = useMemo(
            () => emojiGrid[state.selectedIndex] || null,
            [emojiGrid, state.selectedIndex],
        );

        // --- Keyboard Navigation
        const findFirstEmojiIndex = useCallback(() => {
            let index = 0;
            while (index < emojiGrid.length && typeof emojiGrid[index] !== 'object') {
                index++;
            }
            return typeof emojiGrid[index] === 'object' ? index : -1;
        }, [emojiGrid]);

        const navigateToEmoji = useCallback(
            (startRow, startCol, direction) => {
                let row = startRow;
                let col = startCol;
                let attempts = 0;
                const maxAttempts = emojiGridRowsCount * GRID_COLS;

                do {
                    switch (direction) {
                        case 'ArrowDown':
                            row = row >= emojiGridRowsCount - 1 ? 0 : row + 1;
                            break;
                        case 'ArrowUp':
                            row = row === 0 ? emojiGridRowsCount - 1 : row - 1;
                            break;
                        case 'ArrowRight':
                            if (col < GRID_COLS - 1) {
                                col++;
                            } else {
                                col = 0;
                                row = row >= emojiGridRowsCount - 1 ? 0 : row + 1;
                            }
                            break;
                        case 'ArrowLeft':
                            if (col > 0) {
                                col--;
                            } else {
                                col = GRID_COLS - 1;
                                row = row === 0 ? emojiGridRowsCount - 1 : row - 1;
                            }
                            break;
                        default:
                            break;
                    }
                    attempts++;
                } while (
                    attempts < maxAttempts &&
                    typeof emojiGrid[row * GRID_COLS + col] !== 'object'
                );

                const newIndex = row * GRID_COLS + col;

                if (typeof emojiGrid[newIndex] === 'object') {
                    gridRef.current?.scrollToCell({
                        columnAlign: 'center',
                        rowAlign: 'center',
                        columnIndex: col,
                        rowIndex: row,
                    });
                    dispatch({
                        type: SET_SELECTED,
                        payload: {
                            selectedIndex: newIndex,
                            columnIndex: col,
                            rowIndex: row,
                            hovered: false,
                        },
                    });
                }
            },
            [emojiGrid, emojiGridRowsCount],
        );

        const handleKeyDown = useCallback(
            (event) => {
                const { key } = event;
                const isNavKey = ['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft'].includes(key);
                const isActionKey = key === 'Escape' || key === 'Enter';

                if (!isNavKey && !isActionKey) return;

                event.preventDefault();
                event.stopPropagation();

                if (key === 'Escape') {
                    onVisibleChange?.(false);
                    onRequestFocus?.();
                    return;
                }

                if (key === 'Enter') {
                    if (selectedEmoji) handleClick(selectedEmoji.name);
                    return;
                }

                const activeElement = document.activeElement;
                const searchInput = searchBarRef.current;
                const isSearchFocused =
                    searchInput === activeElement || searchInput?.inputRef === activeElement;

                if (isSearchFocused && key === 'ArrowDown') {
                    gridContainerRef.current?.focus();

                    if (state.selectedIndex === null) {
                        const firstEmojiIndex = findFirstEmojiIndex();
                        if (firstEmojiIndex >= 0) {
                            const row = Math.floor(firstEmojiIndex / GRID_COLS);
                            const col = firstEmojiIndex % GRID_COLS;
                            dispatch({
                                type: SET_SELECTED,
                                payload: {
                                    selectedIndex: firstEmojiIndex,
                                    columnIndex: col,
                                    rowIndex: row,
                                    hovered: false,
                                },
                            });
                            gridRef.current?.scrollToCell({
                                columnAlign: 'center',
                                rowAlign: 'center',
                                columnIndex: col,
                                rowIndex: row,
                            });
                        }
                    } else {
                        navigateToEmoji(state.rowIndex, state.columnIndex, key);
                    }
                    return;
                }

                if (state.selectedIndex === null) {
                    const firstEmojiIndex = findFirstEmojiIndex();
                    if (firstEmojiIndex >= 0) {
                        const row = Math.floor(firstEmojiIndex / GRID_COLS);
                        const col = firstEmojiIndex % GRID_COLS;
                        dispatch({
                            type: SET_SELECTED,
                            payload: {
                                selectedIndex: firstEmojiIndex,
                                columnIndex: col,
                                rowIndex: row,
                                hovered: false,
                            },
                        });
                    }
                } else {
                    navigateToEmoji(state.rowIndex, state.columnIndex, key);
                }
            },
            [
                findFirstEmojiIndex,
                handleClick,
                navigateToEmoji,
                onRequestFocus,
                onVisibleChange,
                selectedEmoji,
                state,
            ],
        );

        // --- Scroll Handling
        const gridSectionsHeights = useMemo(() => {
            let scrollTop = 0;
            return Object.keys(groupedEmojiObj).reduce((obj, key) => {
                let rows = Math.floor(groupedEmojiObj[key].length / GRID_COLS) + 1;
                const extraRow = groupedEmojiObj[key].length % GRID_COLS ? 1 : 0;
                rows = rows + extraRow;
                scrollTop = scrollTop + rows * GRID_ROW_HEIGHT;
                return { ...obj, [key]: scrollTop };
            }, {});
        }, [groupedEmojiObj]);

        const handleScroll = useCallback(
            (event) => {
                const scrollTop = event?.currentTarget?.scrollTop || 0;
                const section = Object.entries(gridSectionsHeights).find(
                    ([, value]) => scrollTop + 10 <= value,
                )?.[0];
                if (section) {
                    dispatch({ type: SET_ACTIVE_SECTION, payload: section });
                }
            },
            [gridSectionsHeights],
        );

        // --- Render
        const popoverContent = useMemo(() => {
            return (
                <div className={classes.root}>
                    <SearchBar
                        className={classes.searchBar}
                        placeholder={texts?.search_placeholder || 'Search all emoji'}
                        useAsSimpleSearch
                        overrides={{
                            Select: {
                                getRef: (ref) => {
                                    searchBarRef.current = ref;
                                },
                                customOnChangeInput: ({ inputValue, action }) => {
                                    if (action.action === 'input-change') {
                                        handleChange(inputValue);
                                    }
                                },
                                onKeyDown: handleKeyDown,
                            },
                        }}
                    />
                    <div
                        ref={gridContainerRef}
                        className={classes.gridWrapper}
                        tabIndex={-1}
                        onKeyDown={handleKeyDown}
                    >
                        {!state.search && (
                            <Text
                                type="captionMedium"
                                className={classNames(classes.section, classes.sectionSticky)}
                            >
                                {state.section}
                            </Text>
                        )}
                        {emojiGridRowsCount ? (
                            <Grid
                                gridRef={gridRef}
                                cellComponent={GridItem}
                                cellProps={{}}
                                columnCount={GRID_COLS}
                                columnWidth={GRID_COLUMN_WIDTH}
                                rowCount={emojiGridRowsCount}
                                rowHeight={GRID_ROW_HEIGHT}
                                style={{
                                    width: GRID_COLUMN_WIDTH * GRID_COLS + 16 + 1,
                                    height: GRID_ROW_HEIGHT * 5.5,
                                }}
                                onScroll={handleScroll}
                            />
                        ) : (
                            <div className={classes.empty}>
                                <Text>{texts?.noResults || 'No Results'}</Text>
                            </div>
                        )}
                    </div>
                    <div className={classes.hint}>
                        {selectedEmoji && (
                            <Fragment>
                                <span className={classes.hintIcon}>{selectedEmoji?.emoji}</span>
                                <Text type="caption">:{selectedEmoji.name}:</Text>
                            </Fragment>
                        )}
                    </div>
                </div>
            );
        }, [
            GridItem,
            classes,
            emojiGridRowsCount,
            handleChange,
            handleKeyDown,
            handleScroll,
            selectedEmoji,
            state.search,
            state.section,
            texts,
        ]);

        return (
            <Popover
                placement="top"
                onVisibleChange={handleVisibleChange}
                content={popoverContent}
                className={classes.popover}
                visible={popoverVisible}
            >
                {children}
            </Popover>
        );
    },
);

EmojiPicker.propTypes = {
    children: PropTypes.node.isRequired,
    classes: PropTypes.object,
    onVisibleChange: PropTypes.func,
    popoverVisible: PropTypes.bool,
    emojis: PropTypes.array,
    texts: PropTypes.shape({
        noResults: PropTypes.string,
        search_placeholder: PropTypes.string,
        frequently_used_emoji: PropTypes.string,
    }),
    defaultFrequentlyUsed: PropTypes.object,
    onSelectEmoji: PropTypes.func,
    onRequestFocus: PropTypes.func,
};

EmojiPicker.defaultProps = {
    classes: undefined,
    onVisibleChange: undefined,
    popoverVisible: false,
    emojis: undefined,
    texts: undefined,
    defaultFrequentlyUsed: undefined,
    onSelectEmoji: undefined,
    onRequestFocus: undefined,
};

export default memo(EmojiPicker);
